package dev.kuml.portal.web

import dev.kuml.portal.web.api.ApiResult
import dev.kuml.portal.web.api.PortalApi
import dev.kuml.portal.web.auth.authScreen
import dev.kuml.portal.web.auth.oauthNoticeFor
import dev.kuml.portal.web.auth.resetPasswordScreen
import dev.kuml.portal.web.auth.verifyEmailScreen
import dev.kuml.portal.web.components.portalHeader
import dev.kuml.portal.web.editor.editorScreen
import dev.kuml.portal.web.i18n.LanguageState
import dev.kuml.portal.web.i18n.t
import dev.kuml.portal.web.legal.agbScreen
import dev.kuml.portal.web.legal.datenschutzScreen
import dev.kuml.portal.web.legal.impressumScreen
import dev.kuml.portal.web.profile.profileScreen
import dev.kuml.portal.web.projects.projectListScreen
import dev.kuml.portal.web.routing.Router
import dev.kuml.portal.web.routing.Screen
import dev.kuml.portal.web.share.publicShareScreen
import io.kvision.Application
import io.kvision.html.div
import io.kvision.panel.Root
import io.kvision.panel.root
import io.kvision.utils.useModule
import kotlinx.browser.window
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.asCoroutineDispatcher
import kotlinx.coroutines.launch
import org.w3c.dom.url.URLSearchParams

/** Shared coroutine scope for every `suspend` API call fired from a click/lifecycle handler --
 * mirrors the equivalent app-wide scope in the sibling `Pepela-Smart-Home/Portal-Server` KVision
 * app. Backed by the browser's own event-loop dispatcher (`window`), not a background thread pool
 * that doesn't exist in a single-threaded Kotlin/JS runtime. */
public val AppScope: CoroutineScope = CoroutineScope(window.asCoroutineDispatcher())

@JsModule("./css/app.css")
public external object AppCss

/**
 * The KVision `Application` entry point -- see `Main.kt` for `startApplication(...)`. Owns the
 * single [Root] container, the [PortalApi] instance (with the 401 -> `/login` interceptor wired to
 * the router), and the [Router] that swaps screens in and out of that root on every route change.
 */
public class PortalApp : Application() {
    private lateinit var rootPanel: Root
    private lateinit var api: PortalApi
    private lateinit var router: Router
    private var pendingLoginNotice: Boolean = false

    /** R-5: the `oauth` query parameter a callback redirect (`/login?oauth=...` or
     * `/profile?oauth=...`) carried on the FULL PAGE navigation that just booted this app --
     * captured once in [start], BEFORE the router consumes the path, then the query string is
     * stripped from the address bar so a reload never re-shows the same notice. Resolved to
     * translated text fresh at render time by [renderScreen] (same "read `t`/`t.*` at build time,
     * never cache across a language change" discipline as [pendingLoginNotice]'s own
     * `t.errors.sessionExpired`), and cleared the same way [pendingLoginNotice] is: only when the
     * screen that showed it is actually LEFT, never when it is merely re-rendered by a language
     * switch. */
    private var pendingOAuthCode: String? = null

    /** R-18: the currently mounted screen, re-rendered wholesale on a language switch (see
     * [LanguageState.onLanguageChanged] wiring in [start]) -- mirrors this class's existing
     * "tear down and rebuild everything" convention for a route change, just triggered by a
     * different event. `null` before the very first [renderScreen] call. */
    private var currentScreen: Screen? = null

    /**
     * Whatever the CURRENTLY mounted screen needs torn down before the next one replaces it --
     * currently just the editor's live [dev.kuml.portal.web.editor.CollabClient] connection (see
     * [dev.kuml.portal.web.editor.EditorScreenHandle]'s KDoc for why this is the actual
     * mount/unmount hook, not `Router.kt`, which has no such concept at all). `null` for every
     * screen that opens nothing that needs closing.
     */
    private var currentScreenDispose: (() -> Unit)? = null

    init {
        useModule(AppCss)
    }

    override fun start() {
        rootPanel = root("kportal")
        LanguageState.initialise()
        LanguageState.onLanguageChanged = { currentScreen?.let { renderScreen(it) } }
        // R-5: captured BEFORE Router.init below ever runs -- Navigo's own route matching only
        // looks at the path, never the query string, so this read is independent of routing order,
        // but doing it first keeps every query-string concern co-located at the top of start().
        val initialSearch = URLSearchParams(window.location.search)
        pendingOAuthCode = initialSearch.get("oauth")
        if (pendingOAuthCode != null) {
            window.history.replaceState(null, "", window.location.pathname)
        }
        api = PortalApi(onSessionExpired = { onSessionExpired() })
        router = Router(onScreen = { screen -> renderScreen(screen) })
    }

    private fun onSessionExpired() {
        pendingLoginNotice = true
        router.navigate("/login")
    }

    private fun renderScreen(screen: Screen) {
        val previousScreen = currentScreen
        currentScreen = screen
        currentScreenDispose?.invoke()
        currentScreenDispose = null
        rootPanel.removeAll()
        // The pending-session-expired notice must only be consumed when the Login screen is
        // actually LEFT for a different screen (real navigation, e.g. successful login) -- not
        // when Login itself is merely re-rendered by a language switch (see
        // `LanguageState.onLanguageChanged` wiring in `start`, which re-invokes `renderScreen`
        // with the SAME `currentScreen`). Clearing it unconditionally on every render of
        // `Screen.Login` would drop the notice the moment the user toggles the language while
        // still looking at it.
        if (previousScreen is Screen.Login && screen !is Screen.Login) {
            pendingLoginNotice = false
        }
        // R-5: same "only consumed when actually LEFT, not on a same-screen re-render" discipline
        // as pendingLoginNotice above -- a pending OAuth code may have landed on EITHER /login or
        // /profile (see pendingOAuthCode's own KDoc), so both are guarded here.
        if ((previousScreen is Screen.Login && screen !is Screen.Login) ||
            (previousScreen is Screen.Profile && screen !is Screen.Profile)
        ) {
            pendingOAuthCode = null
        }
        when (screen) {
            is Screen.Login -> {
                // R-18: the pending-session-expired NOTICE (not a pre-formatted string) survives a
                // language switch that re-renders this same screen -- resolved to the CURRENT
                // language's copy here, at render time, rather than at the moment the 401 fired
                // (see PortalApp.kt's class-level KDoc discipline: read `t`/`t.*` at build time,
                // never cache it across a language change).
                // R-5: the session-expiry notice always wins over a pending OAuth notice -- the two
                // can never legitimately coincide (an OAuth redirect only ever lands here right
                // after `start()`, before any authenticated call could have 401'd), but if they
                // somehow did, "your session expired" is the more actionable message.
                val oauthNotice = oauthNoticeFor(t.auth, pendingOAuthCode)
                val notice = if (pendingLoginNotice) t.errors.sessionExpired else oauthNotice?.message
                val noticeIsError = !pendingLoginNotice && oauthNotice?.isError == true
                rootPanel.authScreen(
                    api,
                    initialNotice = notice,
                    initialNoticeIsError = noticeIsError,
                    onAuthenticated = { router.navigate("/projects") },
                    onImpressumClick = { router.navigate("/impressum") },
                    onDatenschutzClick = { router.navigate("/datenschutz") },
                    onAgbClick = { router.navigate("/agb") },
                )
            }

            is Screen.VerifyEmail -> {
                // R-3: no `portalHeader` -- unauthenticated, exactly like `Screen.Login` and
                // `Screen.PublicShare` below.
                rootPanel.verifyEmailScreen(
                    api,
                    screen.token,
                    onImpressumClick = { router.navigate("/impressum") },
                    onDatenschutzClick = { router.navigate("/datenschutz") },
                    onAgbClick = { router.navigate("/agb") },
                    onContinueToLogin = { router.navigate("/login") },
                )
            }

            is Screen.ResetPassword -> {
                // R-4: no `portalHeader` -- unauthenticated, exactly like `Screen.VerifyEmail` above.
                rootPanel.resetPasswordScreen(
                    api,
                    screen.token,
                    onImpressumClick = { router.navigate("/impressum") },
                    onDatenschutzClick = { router.navigate("/datenschutz") },
                    onAgbClick = { router.navigate("/agb") },
                    onRequestNewLink = { router.navigate("/login") },
                    onDone = { router.navigate("/login") },
                )
            }

            is Screen.ProjectList -> {
                rootPanel.portalHeader(
                    api,
                    onWordmarkClick = { router.navigate("/projects") },
                    onLoggedOut = { router.navigate("/login") },
                    onImpressumClick = { router.navigate("/impressum") },
                    onDatenschutzClick = { router.navigate("/datenschutz") },
                    onAgbClick = { router.navigate("/agb") },
                    onProfileClick = { router.navigate("/profile") },
                )
                rootPanel.div(className = "kportal-page") {
                    projectListScreen(api, onOpenProject = { id -> router.navigate("/projects/$id") })
                }
            }

            is Screen.Editor -> {
                val diagramId = screen.diagramId
                if (diagramId == null) {
                    // R-22: bare `/projects/:id` deep link (no diagram segment) -- resolve the
                    // project's default diagram, then REPLACE the route so the editor screen
                    // itself always mounts with a concrete diagram id. AppScope.launch here is
                    // safe even though nothing is on screen yet: the router's own history
                    // replacement is what actually re-triggers renderScreen once resolved.
                    AppScope.launch {
                        when (val result = api.getProject(screen.projectId)) {
                            is ApiResult.Success -> {
                                val resolvedDiagramId = result.value.defaultDiagramId
                                if (resolvedDiagramId != null) {
                                    router.navigate("/projects/${screen.projectId}/diagrams/$resolvedDiagramId")
                                } else {
                                    // Unreachable in practice (every project has >= 1 diagram) --
                                    // fails back to the project list rather than a blank screen.
                                    router.navigate("/projects")
                                }
                            }
                            else -> router.navigate("/projects")
                        }
                    }
                    return
                }
                val header =
                    rootPanel.portalHeader(
                        api,
                        onWordmarkClick = { router.navigate("/projects") },
                        onLoggedOut = { router.navigate("/login") },
                        onImpressumClick = { router.navigate("/impressum") },
                        onDatenschutzClick = { router.navigate("/datenschutz") },
                        onAgbClick = { router.navigate("/agb") },
                        onProfileClick = { router.navigate("/profile") },
                    )
                rootPanel.div(className = "kportal-page") {
                    val handle =
                        editorScreen(
                            api,
                            screen.projectId,
                            diagramId,
                            onBack = { router.navigate("/projects") },
                            onSwitchDiagram = { newDiagramId ->
                                router.navigate("/projects/${screen.projectId}/diagrams/$newDiagramId")
                            },
                            onUsageChanged = { header.refreshUsage() },
                        )
                    currentScreenDispose = { handle.dispose() }
                }
            }

            is Screen.Profile -> {
                val header =
                    rootPanel.portalHeader(
                        api,
                        onWordmarkClick = { router.navigate("/projects") },
                        onLoggedOut = { router.navigate("/login") },
                        onImpressumClick = { router.navigate("/impressum") },
                        onDatenschutzClick = { router.navigate("/datenschutz") },
                        onAgbClick = { router.navigate("/agb") },
                        onProfileClick = { router.navigate("/profile") },
                    )
                rootPanel.div(className = "kportal-page") {
                    // R-23: the header's own avatar/initials only ever load once at mount --
                    // without this, a rename would leave the header stale until the next full
                    // navigation (see `PortalHeader.refreshIdentity`'s KDoc).
                    // R-5: initialOAuthCode -- see pendingOAuthCode's own KDoc; profileScreen
                    // resolves it to translated text itself (t.profile.*), mirroring how
                    // Screen.Login resolves its own notice above.
                    profileScreen(api, onIdentityChanged = { header.refreshIdentity() }, initialOAuthCode = pendingOAuthCode)
                }
            }

            is Screen.PublicShare -> {
                // R-21: no portalHeader (see publicShareScreen's KDoc) -- exactly like the three
                // legal screens below, which are also reachable without an authenticated session.
                rootPanel.publicShareScreen(
                    api,
                    screen.token,
                    onImpressumClick = { router.navigate("/impressum") },
                    onDatenschutzClick = { router.navigate("/datenschutz") },
                    onAgbClick = { router.navigate("/agb") },
                )
            }

            is Screen.Impressum -> rootPanel.impressumScreen()

            is Screen.Datenschutz -> rootPanel.datenschutzScreen()

            is Screen.Agb -> rootPanel.agbScreen()
        }
    }
}
